import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { X, ArrowRight, Smartphone } from 'lucide-react';
import { api } from '../lib/api';

const selStyle = { width: '100%', padding: '11px 12px', borderRadius: 12, background: 'var(--sf2)', border: '1.5px solid var(--bd)', color: 'var(--t1)', fontFamily: 'Sora', fontWeight: 700, fontSize: 13 };
const lbl = { fontFamily: 'Sora', fontWeight: 800, fontSize: 10.5, color: 'var(--t3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 };

// Transfère toutes les vidéos pas encore postées d'un posteur à un autre, aux mêmes dates.
export default function TransferPosterDialog({ project, tasks, users, onClose, onSaved }) {
  const team = users.filter((u) => u.role !== 'client' && !u.is_archived);
  const pending = tasks.filter((t) => t.kind === 'video' && t.stage !== 'done' && !t.archived);
  const posterOf = (t) => t.assigned_to_poster || project.poster_id || null;
  const current = useMemo(() => [...new Set(pending.map(posterOf).filter(Boolean))], [tasks]); // eslint-disable-line react-hooks/exhaustive-deps
  const [fromId, setFromId] = useState(current[0] || '');
  const [toId, setToId] = useState('');
  const [saving, setSaving] = useState(false);
  const count = pending.filter((t) => posterOf(t) === fromId).length;
  const nameOf = (id) => team.find((u) => u.id === id)?.name || users.find((u) => u.id === id)?.name || id;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/projects/${project.id}/transfer-poster`, { from_id: fromId, to_id: toId });
      toast.success(`${r.data.videos} vidéo(s) transférée(s) à ${nameOf(toId)} ✓ — mêmes dates`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); setSaving(false); }
  };

  return createPortal(
    <div data-testid="transfer-poster-dialog" onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(6,12,26,0.78)', backdropFilter: 'blur(14px)', display: 'grid', placeItems: 'center', padding: 14 }}>
      <div onClick={(e) => e.stopPropagation()} className="scale-in" style={{ width: '100%', maxWidth: 480, borderRadius: 20, padding: 22, background: 'linear-gradient(180deg, rgba(20,29,66,0.92) 0%, rgba(14,22,44,0.98) 100%)', border: '1px solid var(--bd)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <Smartphone size={18} color="#F5C56A" />
          <div style={{ flex: 1, fontFamily: 'Sora', fontWeight: 800, fontSize: 17, color: 'var(--t1)' }}>Modifier le poster</div>
          <button data-testid="transfer-poster-close" onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--bd)', borderRadius: 9, padding: 7, color: 'var(--t2)', cursor: 'pointer' }}><X size={14} /></button>
        </div>
        <div style={{ fontFamily: 'Lexend', fontSize: 12, color: 'var(--t3)', marginBottom: 18 }}>Toutes les vidéos pas encore postées passent au nouveau poster, aux mêmes jours. Les vidéos déjà postées restent dans l&apos;historique.</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 10, alignItems: 'end' }}>
          <div>
            <div style={lbl}>Ancien poster</div>
            <select data-testid="transfer-poster-from" value={fromId} onChange={(e) => setFromId(e.target.value)} style={selStyle}>
              <option value="">—</option>
              {[...new Set([...current, ...team.map((u) => u.id)])].map((id) => <option key={id} value={id}>{nameOf(id)}</option>)}
            </select>
          </div>
          <ArrowRight size={18} color="var(--t3)" style={{ marginBottom: 12 }} />
          <div>
            <div style={lbl}>Nouveau poster</div>
            <select data-testid="transfer-poster-to" value={toId} onChange={(e) => setToId(e.target.value)} style={selStyle}>
              <option value="">Choisir…</option>
              {team.filter((u) => u.id !== fromId).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
        </div>
        <div data-testid="transfer-poster-count" style={{ marginTop: 14, fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5, color: '#F5C56A' }}>
          {fromId ? `${count} vidéo(s) de ${nameOf(fromId)} à transférer` : 'Choisis l\'ancien poster'}
        </div>
        <button data-testid="transfer-poster-save" onClick={save} disabled={saving || !fromId || !toId} className="asa-btn asa-btn-primary" style={{ width: '100%', marginTop: 14 }}>{saving ? 'Transfert…' : 'Transférer les vidéos'}</button>
      </div>
    </div>,
    document.body,
  );
}
